import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { AlertComponent } from "../../common/components";
import { MUITable } from "../../common/components";
import { SupportTicketListProps } from "../afp-support/types";
import _ from "lodash";
import moment from "moment";
import TicketFilter from "./ticket-filters";
import { PaginationComponent } from "../../common/components";
import { useSetLoading } from "../../common/hooks/appearance";
import SupportTicketFilterOptions from "./support-tickets-filter-options/support-ticket-filter-options";
import { convertUTCToLocal } from "../../utils/utils";
import { SelectInputComponent } from "../../common/components";
import { TICKET_STATUS_OPTIONS } from "./support-ticket-constants";
import { actionOptions } from "./support-ticket-constants";
import { useResourcePermissions } from "../../common/hooks/resource-permissions";
import {
  getTicketsList,
  updateTicketStatus,
  getTicketSenders,
} from "../../api/afp-support-tickets";
import MODULES from "../../constants/modules";
import { ConfirmationPopup } from "../../common/components";
import InfoIcon from "../../assets/icons/info-icon.svg";
import MarkAsClosedPopup from "./mark-as-closed-popup";
import ViewTicket from "./view-ticket";
import { fetchUniquePartnerNames } from "../tenant/tenant-list/tenant-list-helper";

const SupportTicketsList: React.FC<SupportTicketListProps> = ({
  status,
  crumbMappingOption,
  setCrumbMappingOption,
  viewOpen,
  setViewOpen,
  setHadActiveSearchAndFilters,
  hadActiveSearchAndFilters,
}) => {
  const [searchText, setSearchText] = React.useState<string>("");
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalRecords, setTotalRecords] = React.useState<number>(0);
  const [data, setData] = React.useState<Array<any>>([]);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [selectedTicketsFilter, setSelectedTicketsFilter] = useState<number[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTicketsMap, setSelectedTicketsMap] = useState<{
    [page: number]: number[];
  }>({});
  const [pageSize] = useState<number>(15);
  const isSearchTriggered = useRef<boolean>(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const [activeFilters, setActiveFilters] = useState<any>({});
  const [selectedTicketStatus, setSelectedTicketStatus] = useState<{
    id: number | null;
    val: string;
  }>({ id: null, val: "" });
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const { canRead, canWrite, canDelete } = useResourcePermissions(
    MODULES.SUPPORT_TICKETS
  );
  const [isMarkAsClosedOpen, setIsMarkAsClosedOpen] = useState<boolean>(false);
  const [isReOpenOpen, setIsReOpenOpen] = useState<boolean>(false);
  const [currentTicketId, setCurrentTicketId] = useState<number | null>(null);
  const [currentTaskSubject, setCurrentTaskSubject] = useState<string>("");
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [senders, setSenders] = useState<string[]>([]);

  const isAllSelected = selectedTicketsMap[currentPage]?.length === data.length;

  const isIndeterminate =
    selectedTicketsMap[currentPage]?.length > 0 &&
    selectedTicketsMap[currentPage]?.length < data.length;

  const handleSelectAll = () => {
    setSelectedTicketsMap((prev) => {
      const currentPageTickets = data
        .filter((item) => item.status !== "Closed-Won")
        .map((item) => item.id);
      const selectedTicketsOnPage = prev[currentPage] || [];

      if (selectedTicketsOnPage.length > 0) {
        return { ...prev, [currentPage]: [] };
      }
      return { ...prev, [currentPage]: currentPageTickets };
    });
  };

  const fetchTickets = async (
    page = 1,
    search: string = "",
    filters: any = {},
    status?: string
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      let params: any = {
        limit: pageSize,
        offset: offset,
      };
      if (!searchText && !(appliedFilterCount > 0) && status) {
        params.tab = status;
      }
      if (filters.dateRaisedStart && filters.dateRaisedEnd) {
        params.dateRaisedStart = filters.dateRaisedStart.startOf('day');
        params.dateRaisedEnd = filters.dateRaisedEnd.endOf('day');
      }
      if (filters.dateClosedStart && filters.dateClosedEnd) {
        params.dateClosedStart = filters.dateClosedStart.startOf('day');
        params.dateClosedEnd = filters.dateClosedEnd.endOf('day');
      }
      const { dateRaisedStart, dateRaisedEnd, dateClosedStart, dateClosedEnd, ...restFilters } = filters;
      if (Object.keys(restFilters).length) {
        params = { ...params, ...restFilters };
      }
      if (search) {
        params.searchText = search;
      }
      const response = await getTicketsList(params);
      setData(response?.data?.data?.tickets || []);
      setTotalRecords(response?.data?.data?.totalRecords);
      if (searchText || appliedFilterCount > 0) {
        setHadActiveSearchAndFilters?.(true);
      } else setHadActiveSearchAndFilters?.(false);
    } catch (error: any) {
      console.error("Error fetching tickets:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to fetch tickets. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const getAlertMessageBasedOnStatus = (status: string) => {
    switch (status) {
      case "Closed":
        return "Ticket closed successfully.";
      case "ReOpen":
        return "Ticket reopened successfully.";
      default:
        return "Ticket status updated successfully.";
    }
  };

  const updateTicketStatusAPI = async (
    ticketId: number,
    data: { status: string; comments?: string }
  ) => {
    try {
      setLoading(true);

      const result = await updateTicketStatus(ticketId, data);
      if (result?.status === 200) {
        setAlertMessage(getAlertMessageBasedOnStatus(data?.status));
        setAlertType("success");
        setAlertOpen(true);
        // Refresh the tickets list
        setCurrentPage(1);
        fetchTicketsIncludingSearchAndFilters();
      } else {
        setAlertMessage("Failed to update ticket status. Please try again.");
        setAlertType("error");
        setAlertOpen(true);
      }
    } catch (error: any) {
      console.error("Error updating ticket status:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to update ticket status. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchTickets(pageToUse, searchText || "", activeFilters, status);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters, status]);

  useEffect(() => {
    if (selectedTicketStatus.id !== null) {
      updateTicketStatusAPI(selectedTicketStatus.id, {
        status: selectedTicketStatus.val === "In Progress" ? "InProgress" : selectedTicketStatus.val,
      });
    }
  }, [selectedTicketStatus]);

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on search
      setSearchText(text);
    }, 500),
    []
  );

  const resetSearch = () => {
    setSearchText("");
    if (searchRef.current) {
      searchRef.current.clear();
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handleCheckboxChange = (id: number) => {
    setSelectedTicketsMap((prev) => {
      const prevSelected = prev[currentPage] || [];
      const newSelected = prevSelected.includes(id)
        ? prevSelected.filter((ticketId) => ticketId !== id)
        : [...prevSelected, id];

      return {
        ...prev,
        [currentPage]: newSelected,
      };
    });
  };

  const handleViewClick = (ticketId: number, subject: string) => {
    setCurrentTicketId(ticketId);
    setCrumbMappingOption([
      {
        label: "Support Tickets",
        link: "#",
        handleClick: (event: any) => {
          event.preventDefault();
          setViewOpen?.(false);
          setCrumbMappingOption?.([...crumbMappingOption?.slice(0, 1)]);
        },
      },
      {
        label: subject,
        link: "",
      },
    ]);
    setViewOpen?.(true);
  };

  const handleAction = (actionType: string, id: number) => {
    setCurrentTicketId(id);
    if (actionType === "View") {
      const subject = data.find((ticket) => ticket.id === id)?.subject || "";
      handleViewClick(id, subject);
    } else if (actionType === "Mark As Closed") {
      // Implement mark as closed functionality here
      setIsMarkAsClosedOpen(true);
    } else if (actionType === "Reopen") {
      //implement re open functionality here
      setIsReOpenOpen(true);
    }
  };

  const handleMarkAsClosed = async (value: string) => {
    // Implement the logic to mark the ticket as closed
    if (currentTicketId) {
      await updateTicketStatusAPI(currentTicketId, {
        status: "Closed",
        comments: value,
      });
      setIsMarkAsClosedOpen(false);
    }
  };

  const handleReOpen = async () => {
    // Implement the logic to reopen the ticket
    if (currentTicketId) {
      await updateTicketStatusAPI(currentTicketId, { status: "ReOpen" });
      setIsReOpenOpen(false);
    }
  };

  const headers = [
    {
      key: "action",
      label: "Action",
    },
    {
      key: "ticketId",
      label: "Ticket ID",
      isSortable: false,
    },
    {
      key: "partnerName",
      label: "Partner Name",
    },
    {
      key: "subject",
      label: "Subject",
    },
    // Include status only if not in 'closed'
    ...(status !== "closed" || hadActiveSearchAndFilters
      ? [
          {
            key: "status",
            label: "Status",
          },
        ]
      : []),
    {
      key: "dateRaised",
      label: "Date Raised",
    },
    ...(status === "closed" || hadActiveSearchAndFilters
      ? [
          {
            key: "dateClosed",
            label: "Date Closed",
          },
        ]
      : []),
    {
      key: "sender",
      label: "Sender",
    },
  ];

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const dateRaisedFilterActive =
      activeFilters.dateRaisedStart || activeFilters.dateRaisedEnd;
    const dateClosedFilterActive =
      activeFilters.dateClosedStart || activeFilters.dateClosedEnd;

    const otherFilters = { ...activeFilters };
    delete otherFilters.dateRaisedStart;
    delete otherFilters.dateRaisedEnd;
    delete otherFilters.dateClosedStart;
    delete otherFilters.dateClosedEnd;

    const otherActiveFilters = Object.values(otherFilters).filter((val) => {
      if (val === undefined || val === "" || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") {
        return Object.values(val).some(
          (v) => v !== "" && v !== undefined && v !== null
        );
      }
      return true;
    }).length;
    if (dateRaisedFilterActive) {
      count += 1;
    }
    if (dateClosedFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [activeFilters]);

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item) => {
        return {
          id: item.id,
          ticketId: item.ticket_id,
          partnerName: item?.tenant?.company_name || "-",
          subject: item.subject,
          status: item.status,
          dateRaised: item.created_at
            ? convertUTCToLocal(item.created_at, "MM/DD/YYYY")
            : "-",
          dateClosed: item.closed_date
            ? convertUTCToLocal(item.closed_date, "MM/DD/YYYY")
            : "-",
          sender: item.raised_by,
        };
      });
    }
    return [];
  }, [data]);

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading tickets...";
    }
    if (searchText && activeFilters && appliedFilterCount > 0) {
      return "No tickets found matching your search and filter criteria";
    }
    if (searchText) {
      return "No tickets found matching your search criteria";
    }
    if (appliedFilterCount > 0) {
      return "No tickets found matching your filter criteria";
    }

    return "No tickets found";
  }, [loading, searchText, activeFilters]);

  const loadCompanies = async () => {
    setLoading(true);
    const companies = await fetchUniquePartnerNames();
    setCompanyNames(companies);
    setLoading(false);
  };

  const fetchSenders = async () => {
    setLoading(true);
    try {
      const response = await getTicketSenders();
      const senderNames = response?.data?.data?.senderNames || [];
      setSenders(senderNames);
    } catch (error: any) {
      console.error("Error fetching ticket senders:", error);
      setAlertMessage(
        error?.response?.data?.message ||
          "Failed to fetch ticket senders. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    fetchSenders();
  }, []);

  const fetchTicketsIncludingSearchAndFilters = () => {
    if (currentPage === 1) {
      if (searchText || appliedFilterCount > 0) {
        fetchTickets(1, searchText || "", activeFilters, "");
      } else fetchTickets(1, searchText || "", activeFilters, status || "");
    } else {
      setCurrentPage(1);
    }
  };

  const formatStatus = (status: string) => {
    switch (status) {
      case "InProgress":
        return "In Progress";
      case "ReOpen":
        return "Reopen";
      default:
        return status;
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);


  return (
    <>
      <div className="filters-and-list-wrapper support-tickets-filters-and-list-wrapper" style={{ display: !viewOpen ? "block" : "none" }}>
        <div>
          <TicketFilter
            isOpenFilter={isOpenFilter}
            setIsOpenFilter={setIsOpenFilter}
            selectedTicketsFilter={appliedFilterCount}
            onSearch={handleSearch}
            searchRef={searchRef}
          />
          {/* Table section */}
          <div className="lead-list-table-filter">
            <div
              className="lead-list-table support-tickets-table"
              style={isOpenFilter ? { width: "80%" } : {}}
            >
              <MUITable
                headers={headers}
                data={formattedData.map((item) => ({
                  key: item.id,
                  action: "verticalEllipse",
                  id: item.id,
                  ticketId: item.ticketId,
                  partnerName: item.partnerName,
                  subject: item.subject,
                  status:
                    item?.status !== "Closed" ? (
                      <SelectInputComponent
                        options={TICKET_STATUS_OPTIONS}
                        value={
                          selectedTicketStatus.id === item.id
                            ? formatStatus(selectedTicketStatus.val)
                            : formatStatus(item.status)
                        }
                        onChangeWithEvent={(event, val: any) => {
                          event.stopPropagation();
                          setSelectedTicketStatus({
                            id: item.id,
                            val: val,
                          });
                        }}
                      />
                    ) : (
                      item.status
                    ),
                  dateRaised: item.dateRaised,
                  dateClosed: item.dateClosed,
                  sender: item.sender,
                  actionOptions: actionOptions(
                    { canRead, canWrite, canDelete },
                    item.status
                  ),
                }))}
                fieldName="afp-support-tickets"
                noResultsTxt={noResultText}
                onRowClick={(rowData: any) =>
                  handleViewClick(rowData?.key, rowData?.subject)
                }
                handleAction={handleAction}
              />
              <MarkAsClosedPopup
                open={isMarkAsClosedOpen}
                onCancel={() => setIsMarkAsClosedOpen(false)}
                onOkay={handleMarkAsClosed}
                title="Mark As Closed"
                description="Are you sure you want to mark this ticket as closed?"
              />
              <ConfirmationPopup
                open={isReOpenOpen}
                onCancel={() => setIsReOpenOpen(false)}
                onOkay={handleReOpen}
                title="Reopen Ticket"
                Icon={InfoIcon}
                buttonText="Reopen"
                note="Are you sure you want to reopen this ticket?"
              />
            </div>
            <div
              style={{ display: isOpenFilter ? "block" : "none" }}
              className="lead-list-filter"
            >
              {isOpenFilter && (
                <SupportTicketFilterOptions
                  open={isOpenFilter}
                  onClose={() => setIsOpenFilter(false)}
                  onApplyFilters={(filterData, shouldClose = true) => {
                    try {
                      setActiveFilters(filterData);
                      if (shouldClose) setIsOpenFilter(false);
                      setCurrentPage(1);
                    } catch (error: any) {
                      console.error("Failed to apply filters:", error);
                      setAlertMessage(
                        error?.message ||
                          "Failed to apply filters. Please try again."
                      );
                      setAlertType("error");
                      setAlertOpen(true);
                    }
                  }}
                  filters={activeFilters}
                  companyNames={companyNames}
                  senders={senders}
                />
              )}
            </div>
            {totalRecords && totalRecords > 0 ? (
              <div className="leads-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      {viewOpen ? <ViewTicket id={currentTicketId} /> : <></>}
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default SupportTicketsList;
